import React, { useState, useEffect } from 'react';
import axios from 'axios';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  styled,
  Button,
  Container,
  TextField,
  Table,
  Modal,
  TableBody,
  TableCell,
  Box,
  Typography,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  Close as CloseIcon,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Updated CustomTableScrollbar with styled API to match Employee modal
const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const OrganizationEditModal = ({
  showEditModal,
  handleEditClose,
  editFormData,
  handleEditChange,
  handleEditSave,
  theme,
}) => {
  return (
    <Modal
      open={showEditModal}
      onClose={handleEditClose}
      BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
    >
      <CustomTableScrollbar
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 700,
          bgcolor: theme.palette.background.paper,
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
            Edit Organization
          </Typography>
          <IconButton onClick={handleEditClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ padding: '20px', overflowY: 'auto', flexGrow: 1 }}>
          <form onSubmit={handleEditSave}>
            <TextField
              label="Organization Name"
              variant="outlined"
              fullWidth
              name="organizationName"
              value={editFormData.organizationName}
              onChange={handleEditChange}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            />
            <TextField
              label="Email"
              variant="outlined"
              fullWidth
              name="email"
              value={editFormData.email}
              onChange={handleEditChange}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            />
            <TextField
              type="tel"
              inputProps={{ maxLength: 10 }}
              label="Mobile No."
              variant="outlined"
              fullWidth
              name="mobileNo"
              value={editFormData.mobileNo}
              onChange={handleEditChange}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            />
            <TextField
              type="tel"
              inputProps={{ maxLength: 10 }}
              label="Landline No."
              variant="outlined"
              fullWidth
              name="landlineNo"
              value={editFormData.landlineNo}
              onChange={handleEditChange}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            />
            <TextField
              label="Address"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              name="address"
              value={editFormData.address}
              onChange={handleEditChange}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            />
            <TextField
              label="Pin Code"
              variant="outlined"
              fullWidth
              name="PinCode"
              value={editFormData.PinCode}
              onChange={handleEditChange}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            />
            <TextField
              label="Website"
              variant="outlined"
              fullWidth
              name="website"
              value={editFormData.website}
              onChange={handleEditChange}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip
                title={
                  !editFormData.organizationName ? 'Enter Organization Name' :
                  !editFormData.email ? 'Enter Email' :
                  !editFormData.mobileNo ? 'Enter Mobile No.' :
                  !editFormData.address ? 'Enter Address' :
                  !editFormData.PinCode ? 'Enter Pin Code' :
                  !editFormData.website ? 'Enter Website' : ''
                }
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleEditSave}
                    sx={{
                      mr: 2,
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                    }}
                    disabled={
                      !editFormData.organizationName ||
                      !editFormData.email ||
                      !editFormData.mobileNo ||
                      !editFormData.address ||
                      !editFormData.PinCode ||
                      !editFormData.website
                    }
                  >
                    UPDATE
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleEditClose}
              >
                CANCEL
              </Button>
            </Box>
          </form>
        </Box>
      </CustomTableScrollbar>
    </Modal>
  );
};

const Organization = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const storedUser = JSON.parse(
  sessionStorage.getItem('user') || localStorage.getItem('user') || '{}'
);
const isClientAdmin = storedUser?.role === 'client_admin';
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState({ OrganizationId: null, OrganizationName: '' });
  const [editFormData, setEditFormData] = useState({
    organizationName: '',
    email: '',
    mobileNo: '',
    landlineNo: '',
    address: '',
    PinCode: '',
    website: '',
  });
  const [organizations, setOrganizations] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all organizations
  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/api/master/organizations/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const mappedOrganizations = response.data.map(org => ({
        OrganizationId: org.id,
        OrganizationName: org.name,
        email: org.email,
        mobile: org.mobile,
        landline: org.landline || '',
        address: org.address || '',
        PinCode: org.pin_code || '',
        website: org.website || '',
      }));

      const storedUser =
        JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "{}");

      const loggedOrganizationId =
        storedUser?.organization_id ??
        storedUser?.organization?.id ??
        storedUser?.organization ??
        "";

      const loggedOrganizationData = loggedOrganizationId
        ? mappedOrganizations.filter(
            (org) => String(org.OrganizationId) === String(loggedOrganizationId)
          )
        : mappedOrganizations;

      setOrganizations(loggedOrganizationData);
      setFilteredData(loggedOrganizationData);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to fetch organizations.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete an organization
  const handleDelete = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      await axios.delete(`${API_BASE_URL}/api/master/organizations/${selectedOrganization.OrganizationId}/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const updatedOrganizations = organizations.filter((org) => org.OrganizationId !== selectedOrganization.OrganizationId);
      setOrganizations(updatedOrganizations);
      setFilteredData(updatedOrganizations);
      setShowModal(false);
      setSnackbarMessage('Organization deleted successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to delete organization.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  const handleAddOrganization = () => {
    navigate('/add-organization');
  };

  const handleEdit = (OrganizationId) => {
    const organization = organizations.find((org) => org.OrganizationId === OrganizationId);
    if (organization) {
      setEditFormData({
        organizationName: organization.OrganizationName,
        email: organization.email,
        mobileNo: organization.mobile,
        landlineNo: organization.landline,
        address: organization.address,
        PinCode: organization.PinCode,
        website: organization.website,
      });
      setShowEditModal(true);
    }
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleEditClose = () => {
    setShowEditModal(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (
      !editFormData.organizationName ||
      !editFormData.email ||
      !editFormData.mobileNo ||
      !editFormData.address ||
      !editFormData.PinCode ||
      !editFormData.website
    ) {
      setSnackbarMessage('Please fill all required fields');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    try {
      const accessToken = localStorage.getItem('accessToken');
      const organizationToUpdate = organizations.find(org => org.OrganizationId === selectedOrganization.OrganizationId);

      const response = await axios.put(
        `${API_BASE_URL}/api/master/organizations/${organizationToUpdate.OrganizationId}/`,
        {
          name: editFormData.organizationName,
          email: editFormData.email,
          mobile: editFormData.mobileNo,
          landline: editFormData.landlineNo,
          address: editFormData.address,
          pin_code: editFormData.PinCode,
          website: editFormData.website,
        },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      const updatedOrganizations = organizations.map(org =>
        org.OrganizationId === organizationToUpdate.OrganizationId ? {
          ...org,
          OrganizationName: response.data.name,
          email: response.data.email,
          mobile: response.data.mobile,
          landline: response.data.landline,
          address: response.data.address,
          PinCode: response.data.pin_code,
          website: response.data.website,
        } : org
      );

      setOrganizations(updatedOrganizations);
      setFilteredData(updatedOrganizations);
      setShowEditModal(false);
      setSnackbarMessage('Organization updated successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to update organization.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      navigate('/login');
    } else {
      fetchOrganizations();
    }
  }, [navigate]);

  useEffect(() => {
    const filtered = organizations.filter((organization) =>
      organization.OrganizationName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      organization.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      organization.mobile.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
  }, [searchTerm, organizations]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: theme.palette.primary.main,
                      marginLeft: 1.5,
                      fontSize: 24,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    sx={{
                      color: "grey",
                      marginRight: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '30px',
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.15)'
                  : 'rgba(255, 255, 255, 1)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                  : '0 2px 8px rgba(0, 0, 0, 0.1)',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'rgba(240, 240, 240, 1)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 2px 12px rgba(0, 0, 0, 0.4)'
                    : '0 2px 12px rgba(0, 0, 0, 0.15)',
                },
                '&.Mui-focused': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.25)'
                    : 'rgba(255, 255, 255, 1)',
                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: `2px solid ${theme.palette.primary.main}`,
                  },
                },
                paddingLeft: 1.5,
                height: '50px',
                transition: 'all 0.3s ease',
              },
            }}
            sx={{
              width: 350,
              maxWidth: '100%',
              '& .MuiInputBase-input': {
                padding: '10px 14px',
                color: theme.palette.text.primary,
                fontWeight: 500,
              },
            }}
          />
         <Box
  sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  }}
>
  {isClientAdmin && (
    <Button
      variant="contained"
      onClick={handleAddOrganization}
      sx={{
        width: '185px',
        borderRadius: '10px',
        height: '50px',
        padding: '10px 0',
        fontWeight: 'bold',
        backgroundColor: theme.palette.primary.main,
        '&:hover': { backgroundColor: theme.palette.primary.dark },
      }}
      startIcon={<AddIcon />}
    >
      ADD ORGANIZATION
    </Button>
  )}
</Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0,
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px', backgroundColor: theme.palette.background.paper }}>
            <Table
              sx={{
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Organization Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Email-Id</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Mobile No</TableCell>
                  {isClientAdmin && ( <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      backgroundColor: theme.palette.mode === 'dark' ? '#064b7a' : theme.palette.primary.dark,
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    Action
                  </TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={isClientAdmin ? 5 : 4} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((organization, index) => (
                    <TableRow
                      key={organization.OrganizationId}
                      sx={{
                        backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{organization.OrganizationName}</TableCell>
                      <TableCell>{organization.email}</TableCell>
                      <TableCell>{organization.mobile}</TableCell>
                       {isClientAdmin && (
                      <TableCell align="center">
                        <span>
<IconButton
onClick={() => navigate(`/edit-organization/${organization.OrganizationId}`)}
  sx={{ color: theme.palette.primary.light }}
>
  <EditIcon />
</IconButton>

                        </span>
                        <span style={{ display: "none" }}>
<IconButton
                            color="error"
                            onClick={() => {
                              setSelectedOrganization({
                                OrganizationId: organization.OrganizationId,
                                OrganizationName: organization.OrganizationName
                              });
                              setShowModal(true);
                            }}
                            sx={{
                              '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>

                        </span>
                      </TableCell>)}
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{
                    backgroundColor: theme.palette.background.paper,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}>
                    <TableCell colSpan={isClientAdmin ? 5 : 4} align="center" sx={{ fontSize: '1rem', padding: '20px', wordSpacing: 7 }}>
                      No Data Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <KeyboardDoubleArrowLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <KeyboardDoubleArrowRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>
                Rows
              </InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Modal open={showModal} onClose={handleClose} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: theme.palette.primary.contrastText }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete {selectedOrganization.OrganizationName}?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <OrganizationEditModal
        showEditModal={showEditModal}
        handleEditClose={handleEditClose}
        editFormData={editFormData}
        handleEditChange={handleEditChange}
        handleEditSave={handleEditSave}
        theme={theme}
      />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Modal
        open={showUserGuideModal}
        onClose={() => setShowUserGuideModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 800,
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Organization Management - User Guide
            </Typography>
            <IconButton
              onClick={() => setShowUserGuideModal(false)}
              sx={{ color: '#fff', position: 'absolute', right: 14 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

         <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
          {isClientAdmin && (
  <>
  {/* Section 1: Add Organization (Button hidden as requested) */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
  >
    ➤ Follow these steps to add a new organization:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      To add a new organization, navigate to the <strong>/add-organization</strong> page using the sidebar or menu.
    </li>
    <li>
      Fill in the required fields: <strong>Organization Name</strong>, <strong>Email</strong>, <strong>Mobile No.</strong>, <strong>Landline No.</strong>, <strong>Address</strong>, <strong>Pin Code</strong>, and <strong>Website</strong>.
    </li>
    <li>
      Before clicking <strong>"SAVE"</strong>, review all entered information for accuracy.
    </li>
    <li>
      Click <strong>"SAVE"</strong> to add the organization. A success notification will confirm the addition.
    </li>
    <li>
      The new organization will appear in the Organization List table after you refresh the page or are redirected.
    </li>
    <li>
      To clear the form, click <strong>"RESET"</strong> (if available).
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Organization Name, Email, Mobile No., Address, Pin Code, and Website are mandatory fields.
  </Typography>

  {/* Section 2: Edit Organization */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to update an existing organization:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Select the organization you want to update from the Organization List table.
    </li>
    <li>
      Click the <strong>Edit</strong> icon (pencil) in the Action column to go to the edit page.
    </li>
    <li>
      Update the details: <strong>Organization Name</strong>, <strong>Email</strong>, <strong>Mobile No.</strong>, <strong>Landline No.</strong>, <strong>Address</strong>, <strong>Pin Code</strong>, and <strong>Website</strong> as needed.
    </li>
    <li>
      Before clicking <strong>"UPDATE"</strong>, ensure all required fields are filled.
    </li>
    <li>
      Click <strong>"UPDATE"</strong> to save your changes. A success notification will confirm the update.
    </li>
    <li>
      To discard changes, click <strong>"CANCEL"</strong>.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Organization Name, Email, Mobile No., Address, Pin Code, and Website are required fields.
  </Typography>

  {/* Section 3: Delete Organization (Button hidden as requested) */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to delete an organization:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Locate the organization you want to delete in the Organization List table.
    </li>
    <li>
      The Delete button is hidden for this page.
    </li>
    <li>
      Delete actions are not available from the visible UI.
    </li>
    <li>
      The organization will be removed from the list, and a success notification will appear.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Deleted organizations cannot be recovered. Proceed with caution.
  </Typography>
  </>)}

  {/* Section 4: Search Organizations */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to search for an organization:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Use the search bar at the top left of the Organization List.
    </li>
    <li>
      Enter the <strong>Organization Name</strong>, <strong>Email</strong>, or <strong>Mobile No.</strong> (or part of it) to filter the table.
    </li>
    <li>
      The table will update automatically to display matching organizations.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: The search is case-insensitive and filters by Organization Name, Email, or Mobile No.
  </Typography>

  {/* Section 5: Pagination */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to navigate the organization list:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Use the pagination controls at the bottom to navigate through the organization list.
    </li>
    <li>
      Click the left (<strong>&lt;&lt;</strong>) or right (<strong>&gt;&gt;</strong>) arrows to move between pages.
    </li>
    <li>
      Select a specific page number to jump directly to that page.
    </li>
    <li>
      Adjust the number of rows per page using the dropdown menu (e.g., 5, 10, 25, 50 rows).
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: The table displays only the selected number of rows per page.
  </Typography>
</CustomTableScrollbar>

        </Box>
      </Modal>
    </Container>
  );
};

export default Organization;
